import { useFormikContext } from 'formik'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'
import React, { useEffect, useState } from 'react'
import { TemplateFormContainer } from './style'
import { TaskElementProps } from './Actions'
import { TemplateDetailSchema } from '../TemplateDetail'
import { CampaignTaskElements } from 'services/api/campaignTemplates'
import OnFormikValueChange from '../OnFormikValueChange'
import { updateCampaignTaskElements, CampaignTemplateStep } from 'state/campaignTemplates'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import TemplateTask from './TemplateTask'
import { fieldKeyByTasks } from '../TemplateDetail/options'
import { selectCampaignByTemplateData } from 'state'
import {
    updateVideoCreationInterpolationListData,
    updateVideoCreationStep1Data,
} from 'state/appPreview/action/videoCreation'
import { selectTemplateFirstStepDataByTaskIndex } from 'state/appPreview/selector'
import { formatTaskValues } from 'components/utils/interpolation'
import { useNewTranslation } from 'hooks'

const fieldNames = fieldKeyByTasks.video_creation

const VideoCreationTask = ({
    showError: baseShowError,
    taskOrder,
    updateCurrentStepStatus,
    setNavigationDisable,
    isReadOnly,
    ...props
}: TaskElementProps) => {
    const { values, setFieldTouched } = useFormikContext<TemplateDetailSchema>()
    const [showError, setShowError] = useState(baseShowError)
    const dispatch = useDispatch()
    const [t] = useNewTranslation()
    const campaign = useSelector(selectCampaignByTemplateData)
    const selectedTemplateFirstStepData = useSelector(selectTemplateFirstStepDataByTaskIndex(taskOrder))
    const isCampaignLive = campaign?.status === CAMPAIGN_STATUS_ENUM.LIVE

    const namePrefix = `task_elements.${taskOrder}.`

    const validateFieldsKey = fieldNames.map(field => namePrefix + field) as (keyof TemplateDetailSchema)[]
    const disableAutoSave = () => {
        // Enable data capture and preview update if campaign is live
        if (isCampaignLive) {
            return false
        }

        return isReadOnly
    }
    const wrongInputCopy = t('errorMessage.validationPostiveInteger')

    useEffect(() => {
        handleDebouncedChange(values)
    }, [])

    useEffect(() => {
        setShowError(baseShowError)
    }, [baseShowError])

    useEffect(() => {
        setFieldTouched(namePrefix + 'number_of_videos', true)
    }, [values.task_elements[taskOrder].number_of_videos])

    const onSaveData = (fieldsValues: Partial<TemplateDetailSchema>, showSuccessToast: boolean) => {
        const validTemplateDataUuid = typeof props.templateUuid === 'string' ? props.templateUuid : ''
        const purchaseTaskValues = (fieldsValues?.task_elements?.[taskOrder] ?? {}) as CampaignTaskElements
        dispatch(
            updateCampaignTaskElements({
                templateUuid: validTemplateDataUuid,
                ...purchaseTaskValues,
                setNavigationDisable: setNavigationDisable,
                showSuccessToast,
            }),
        )
    }

    const handleDebouncedChange = (fieldsValues: Partial<TemplateDetailSchema>) => {
        const taskValue = (fieldsValues?.task_elements?.[taskOrder] ?? {}) as CampaignTaskElements
        if (taskValue.element) {
            const dos = selectedTemplateFirstStepData.step?.blocks?.[0]
            const donts = selectedTemplateFirstStepData.step?.blocks?.[1]
            const textOverlayDos = selectedTemplateFirstStepData.step?.blocks?.[2]
            const textOverlayDonts = selectedTemplateFirstStepData.step?.blocks?.[3]

            const payload = {
                videoDosTitle: dos?.title ?? '',
                videoDos: dos?.description ?? '',
                videoDontsTitle: donts?.title ?? '',
                videoDonts: donts?.description ?? '',
                videoOverlayDosTitle: textOverlayDos?.title ?? '',
                videoTextOverlayDos: textOverlayDos?.description ?? '',
                videoOverlayDontsTitle: textOverlayDonts?.title ?? '',
                videoTextOverlayDonts: textOverlayDonts?.description ?? '',
                exampleVideos: taskValue.example_medias,
                stepTitle: selectedTemplateFirstStepData.step?.title,
                taskTitle: selectedTemplateFirstStepData.taskTitle,
            }

            const uniqueSellingPoints = formatTaskValues(taskValue?.unique_selling_point, {
                isList: true,
                bulletPoint: '\u002D',
            })

            const interpolationListPayload = {
                videoDos: {
                    template: dos?.description ?? '',
                    values: [
                        {
                            number_of_videos: taskValue.number_of_videos || '',
                            emptyValue: t('campaign.template.numberOfVideo.appPreview.placeholder'),
                            isInline: true,
                        },
                        {
                            unique_selling_point: uniqueSellingPoints,
                            emptyValue: '\u002D ' + t('campaign.template.keyPoints.appPreview.placeholder'),
                        },
                        {
                            mandatory_guidelines: taskValue.mandatory_guidelines,
                            emptyValue: t('campaign.template.contentAngle.appPreview.placeholder'),
                        },
                    ],
                },
                videoTextOverlayDos: {
                    template: textOverlayDos?.description ?? '',
                    values: [
                        {
                            unique_selling_point: uniqueSellingPoints,
                            emptyValue: '\u002D ' + t('campaign.template.keyPoints.appPreview.placeholder'),
                        },
                    ],
                },
            }
            dispatch(updateVideoCreationStep1Data(payload))
            dispatch(updateVideoCreationInterpolationListData(interpolationListPayload))

            if (!isCampaignLive && !isReadOnly) {
                onSaveData(fieldsValues, false)
            }
        }
    }

    const onShowError = () => {
        setShowError(true)
    }

    return (
        <TemplateFormContainer data-testid="test-video-creation-task-page">
            <OnFormikValueChange
                callback={handleDebouncedChange}
                fields={validateFieldsKey}
                skipCallbackError={{
                    [namePrefix + 'number_of_videos']: [wrongInputCopy],
                }}
                validateSpecificField={props.validateSpecificField}
                showError={showError}
                updateCurrentStepStatus={updateCurrentStepStatus}
                // Only disableNavigation for input if campaign is not live
                {...(!isCampaignLive && { setDisableNavigation: setNavigationDisable })}
                disableAutoSave={disableAutoSave()}
            />
            <TemplateTask
                elementKey="video_creation"
                field_keys={fieldNames}
                taskOrder={taskOrder}
                updateCurrentStepStatus={updateCurrentStepStatus}
                showError={showError}
                identifierPrefix={CampaignTemplateStep.VIDEO_CREATION}
                onSaveData={onSaveData}
                onShowError={onShowError}
                fields={validateFieldsKey}
                {...props}
            />
        </TemplateFormContainer>
    )
}

export default VideoCreationTask
